import { Stack, Typography } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import StatusBar from '@/designs-dev/device/StatusBar';
import { type HandoverSpec } from '@/designs-dev/handover/types';
import AccountBar from '@/karehero-app/access/AccountBar';
import { options } from '@/karehero-app/handover/dynamic-flow-app';
import {
  NAME,
  chat,
  homeTab,
} from '@/karehero-app/handover/dynamic-flow-slices';
import AppTab from '@/karehero-app/shell/AppTab';
import ChatOption from '@/karehero-app/tell-us-once/intake/ChatOption';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';
import FactList, {
  type FactGroup,
} from '@/karehero-app/tell-us-once/intake/FactList';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import HomeConcept2 from '@/karehero-app/tell-us-once/intake/HomeConcept2';

const noop = () => {};

const placeholder = (
  <Note title='Placeholder' variant='body2'>
    Wording to come.
  </Note>
);

// The lead on Home: one card, one button, and the reason it is first.
const lead = (label: string, reason: string, action: string) => (
  <HomeConcept2
    todo
    conversations={false}
    todoList={{ label, body: reason, items: [], action }}
  />
);

const toldUs = (items: string[]) => (
  <FromUsCard
    label={`What you've told us`}
    items={items}
    sentence
    onEdit={noop}
  >
    <ChatOption label='See everything you told us' onClick={noop} />
  </FromUsCard>
);

const carryOn = (rows: string[]) => (
  <FromUsCard label='Carry on' sentence>
    <Stack spacing={1}>
      {rows.map((r) => (
        <ChatOption key={r} label={r} onClick={noop} />
      ))}
    </Stack>
  </FromUsCard>
);

const TOLD = [
  `${NAME}, 82, lives in England`,
  'Needs help day and night',
  `You can't speak to the bank for ${NAME}`,
];

// The last onboarding question, in onboarding 4.x's patterns: the answers
// attached to the ask, and I need help now as the quieter row under them,
// since it is a way out rather than an answer. 4.x's attached menu is pick
// one, so more than one is a follow-up, Anything else?, rather than a new
// pick-several component.
const NEEDS = [
  'Money and benefits',
  'Finding care',
  'Power of Attorney or legal',
  'Something else',
];
const LAST: ChatQuestion = {
  id: 'last',
  ask: 'What would you like help with?',
  mode: 'say',
  attachRows: { more: ['I need help now'] },
  asides: NEEDS.map((label) => ({ label })),
};
const ANYTHING_ELSE: ChatQuestion = {
  id: 'last-more',
  ask: 'Anything else?',
  mode: 'say',
  attachRows: { more: ["No, that's everything"] },
  asides: NEEDS.filter((n) => n !== 'Money and benefits').map((label) => ({
    label,
  })),
};

// A blocker met inside a conversation: asked there, rather than failing.
const BLOCKER: ChatQuestion[] = [
  {
    id: 'act-for',
    says: [`It looks like ${NAME} could get Attendance Allowance.`],
    ask: `Before we can claim for ${NAME}, we need to know you can act for them.`,
    mode: 'say',
    attachRows: {},
    asides: [
      { label: `${NAME} is happy for me to` },
      { label: 'I have Power of Attorney' },
      { label: `${NAME} can't decide any more` },
      { label: 'Not sure' },
    ],
  },
];

// Profile, as James described it: everything they've told us, grouped by
// segment, each fact saying where it came from, and not sure kept rather
// than thrown away. Built on the master Profile's About list (FactList); the
// source line is the row's own note.
const PROFILE: FactGroup[] = [
  {
    label: `About ${NAME}`,
    rows: [
      {
        id: 'age',
        label: 'Age',
        value: '82',
        note: 'From onboarding, 22 Oct',
        from: 'ai',
      },
      {
        id: 'where',
        label: 'Lives',
        value: 'At home, in Leeds',
        note: 'From onboarding, 22 Oct',
        from: 'ai',
      },
    ],
  },
  {
    label: 'Care needs',
    rows: [
      {
        id: 'help',
        label: 'Help needed',
        value: 'Day and night',
        note: 'From onboarding, 22 Oct',
        from: 'ai',
      },
      {
        id: 'falls',
        label: 'Falls this year',
        value: 'Not sure',
        note: "Not sure yet. We'll ask again next time",
        control: 'choice',
        choices: ['None', 'Once', 'More than once', 'Not sure'],
      },
    ],
  },
  {
    label: 'Money',
    rows: [
      {
        id: 'savings',
        label: 'Savings',
        value: 'Under £16,000',
        note: 'Dave changed this, 22 Oct',
      },
      {
        id: 'benefits',
        label: 'Benefits',
        value: 'None yet',
        note: 'From onboarding, 22 Oct',
        from: 'ai',
      },
    ],
  },
  {
    label: 'Legal',
    rows: [
      {
        id: 'poa',
        label: 'Power of Attorney',
        value: 'Not set up',
        note: 'A care expert added this, 22 Oct',
      },
    ],
  },
];

const TOLD_CHANGE: ChatQuestion[] = [
  {
    id: 'what-changed',
    ask: "What's changed?",
    mode: 'say',
    answer: 'Mum moved into a care home last week',
  },
  {
    id: 'changes',
    says: ["Thanks. Here's what that changes."],
    card: {
      label: 'Does this look right?',
      sentence: true,
      editable: true,
      items: [
        `${NAME} lives in a care home, since 15 Oct`,
        "Who pays for it: we don't know yet",
      ],
    },
    ask: '',
    mode: 'say',
    quickReplies: [
      { label: "Yes, that's right" },
      { label: 'Not quite', secondary: true },
    ],
  },
];

/**
 * When someone tells us more than one thing. What Home leads with, how a
 * conversation handles something that blocks it, and what coming back looks
 * like. The visuals for the whiteboard's When they need more than one thing:
 * an order of rules picks one lead and up to two more, and what blocks the
 * rest goes first.
 */
export const dynamicFlowBrainstormSpec: HandoverSpec = {
  id: 'dynamic-flow-brainstorm',
  title: 'Brainstorm: when they need more than one thing',
  description: '',
  canvas: true,
  links: [
    {
      label: 'Joining up',
      url: '/tell-us-once/flows/joining-up',
    },
    {
      label: 'Across the app',
      url: '/tell-us-once/flows/dynamic-flow-app',
    },
  ],
  flows: [
    {
      title: 'Picking more than one',
      arrows: false,
      numbered: false,
      note: (
        <Note title='Readme' variant='body2'>
          One answer at a time, as onboarding 4.x asks everything, then Anything
          else? until they say that's everything. The order they answer in
          breaks the last tie. I need help now overrides everything else.
        </Note>
      ),
      steps: [
        {
          id: 'bs-last-question',
          title: 'The last onboarding question',
          render: () => chat([LAST], 0),
          note: placeholder,
        },
        {
          id: 'bs-anything-else',
          title: 'Anything else?',
          render: () =>
            chat([{ ...LAST, answer: 'Money and benefits' }, ANYTHING_ELSE], 1),
          note: placeholder,
        },
      ],
    },
    {
      title: 'First Home',
      arrows: false,
      numbered: false,
      beside: true,
      note: (
        <Note title='How the lead is picked' variant='body2'>
          <Stack spacing={1}>
            <Typography variant='body2'>
              Urgent first, then whatever blocks the rest, then what they asked
              for, then what is time-sensitive, then most likely and least
              effort, then the order they said things in.
            </Typography>
            <Typography variant='body2'>
              One lead and up to two more. Everything else waits in Care.
            </Typography>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'bs-home-multi',
          title: 'More than one need: the blocker leads',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {lead(
                  'Set up Power of Attorney',
                  `Because you said you can't speak to the bank for ${NAME}.`,
                  'Start',
                )}
                {options('Also for you', [
                  'Find a care home. Funding check first',
                  `Check what ${NAME} could get`,
                ])}
                {toldUs(TOLD)}
              </Stack>,
            ),
          note: placeholder,
        },
        {
          id: 'bs-home-urgent',
          title: 'Urgent: the call, then what they told us',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {lead(
                  'Speak to a care expert today',
                  "Because you said it's urgent. Next calls at 2pm and 3:30pm.",
                  'Book a call',
                )}
                {toldUs(TOLD)}
              </Stack>,
            ),
          note: placeholder,
        },
        {
          id: 'bs-home-exploring',
          title: 'Exploring: a plan ahead list',
          render: () =>
            homeTab(
              <FromUsCard
                label='Plan ahead'
                body="Because you're thinking ahead. One step at a time."
                sentence
                items={[
                  'Tell us about who you might care for one day',
                  'See what help there is',
                  'Set up Power of Attorney while it is simple',
                  'Take a Circle course',
                ]}
                checked={1}
              />,
            ),
          note: placeholder,
        },
      ],
    },
    {
      title: 'A blocker in the conversation',
      arrows: true,
      steps: [
        {
          id: 'bs-blocker',
          title: 'Asked there and then',
          render: () => chat(BLOCKER, 0),
          note: placeholder,
        },
        {
          id: 'bs-waiting',
          title: 'Paused: waiting on what blocks it',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                {lead(
                  'Set up Power of Attorney',
                  `Because ${NAME}'s Attendance Allowance claim is waiting on it.`,
                  'Start',
                )}
                {carryOn([
                  'Attendance Allowance claim · Waiting on Power of Attorney',
                ])}
              </Stack>,
            ),
          note: placeholder,
        },
      ],
    },
    {
      title: 'Coming back',
      arrows: false,
      numbered: false,
      beside: true,
      steps: [
        {
          id: 'bs-return',
          title: 'The order has changed, and says why',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  We&apos;ve moved things round because Power of Attorney is
                  underway.
                </Typography>
                {lead(
                  `Check what ${NAME} could get`,
                  'Because you said money is tight. Every week before you claim is money lost.',
                  'Check eligibility',
                )}
                {carryOn(['Power of Attorney · 2 of 5 done'])}
                {options('Also for you', ['Find a care home'])}
              </Stack>,
            ),
          note: placeholder,
        },
        {
          id: 'bs-care',
          title: 'Care: everything, with its status',
          render: () => (
            <AppTab active='care'>
              <StatusBar />
              <AccountBar onSignOut={noop} />
              <Stack
                sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 2 }}
                spacing={2}
              >
                <ClaimRow
                  icon='file-contract'
                  name='Power of Attorney'
                  description='Lets you act for them with the bank'
                  count='2 of 5 done'
                  lead
                />
                <ClaimRow
                  icon='hand-holding-heart'
                  name='Attendance Allowance'
                  description='£76.70 to £114.60 a week'
                  count='Waiting on Power of Attorney'
                />
                <ClaimRow
                  icon='calculator'
                  name='Benefits calculator'
                  description={`What else ${NAME} could get`}
                  count='Not started'
                />
                <ClaimRow
                  icon='magnifying-glass'
                  name='Finding a care home'
                  description='Funding check first'
                  count='Not started'
                />
              </Stack>
            </AppTab>
          ),
          note: placeholder,
        },
      ],
    },
    {
      // J-Claude #27: Profile is where everything they've told us lives,
      // edited by talking to it or by hand. A change reruns the Home order and
      // marks results that may now be out of date.
      heading: 'Profile',
      title: "Profile: everything they've told us",
      arrows: true,
      steps: [
        {
          id: 'bs-profile',
          title: 'Where each fact came from',
          render: () => (
            <AppTab active='profile'>
              <StatusBar />
              <AccountBar onSignOut={noop} />
              <Stack
                sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 2 }}
                spacing={2}
              >
                <ChatOption label="Tell us what's changed" onClick={noop} />
                <FactList groups={PROFILE} />
              </Stack>
            </AppTab>
          ),
          note: placeholder,
        },
        {
          id: 'bs-profile-edit',
          title: 'Changing one by hand',
          render: () => (
            <AppTab active='profile'>
              <StatusBar />
              <AccountBar onSignOut={noop} />
              <Stack
                sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 2 }}
                spacing={2}
              >
                <ChatOption label="Tell us what's changed" onClick={noop} />
                <FactList groups={PROFILE} initialEditing='falls' />
              </Stack>
            </AppTab>
          ),
          note: placeholder,
        },
        {
          id: 'bs-profile-talk',
          title: 'Telling it what changed',
          render: () => chat(TOLD_CHANGE, 1),
          note: placeholder,
        },
        {
          id: 'bs-profile-home',
          title: 'Home works the order out again',
          render: () =>
            homeTab(
              <Stack spacing={3}>
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  We&apos;ve updated Home because {NAME} moved into a care home.
                </Typography>
                {lead(
                  "Tell us who's paying for the care home",
                  'Because Attendance Allowance stops after 28 days if the council pays.',
                  'Start',
                )}
                {options('Also for you', ['Set up Power of Attorney'])}
              </Stack>,
            ),
          note: placeholder,
        },
        {
          id: 'bs-profile-stale',
          title: 'Results that may be out of date',
          render: () => (
            <AppTab active='care'>
              <StatusBar />
              <AccountBar onSignOut={noop} />
              <Stack
                sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 2 }}
                spacing={2}
              >
                <ClaimRow
                  icon='calculator'
                  name='Benefits calculator'
                  description={`Worked out before ${NAME} moved`}
                  count='May be out of date'
                  lead
                />
                <ClaimRow
                  icon='hand-holding-heart'
                  name='Attendance Allowance'
                  description='£76.70 to £114.60 a week'
                  count='Check who pays for the care home'
                />
              </Stack>
            </AppTab>
          ),
          note: placeholder,
        },
      ],
    },
  ],
  states: [],
  components: {},
};
